import { PublicFaqStaticContentClient, IFaqStaticContent } from "@studyportals/domain-client";
import { FilterKey, TuitionFeeFilterOptionValue, AttendanceFilterOptionValue } from '@studyportals/search-filters/server-side';
import { IEventAggregationService, ISubscriber } from "@studyportals/event-aggregation-service-interface";
import { ICache, SearchPageCacheReadyEvent } from "@/search-page-interface";
import { Filter } from "../models/filter";
import { EventAggregationService } from "@/search-page/platform/EventAggregationService";
import { GroguFilterKeys } from "./GroguFilterKeys.interface";
import { Content } from "../models/grogu-static-content/Content";

const groguPropertyMapper: Partial<Record<FilterKey, string>> = {
	[FilterKey.COUNTRY]: 'countryId',
	[FilterKey.CONTINENT]: 'continentId',
	[FilterKey.AREA]: 'areaId',
	[FilterKey.DISCIPLINES]: 'disciplineId',
	[FilterKey.DELIVERY_METHOD]: 'attendance',
	[FilterKey.TUITION_FEE]: 'tuitionFee'
};

declare const DOMAIN_API_URL: string;

class FaqStaticContentPresenter implements ISubscriber<SearchPageCacheReadyEvent> {

	private cache: ICache | null = null;
	private client: PublicFaqStaticContentClient;

	public constructor(
		eventAggregationService: IEventAggregationService
	) {
		this.client = new PublicFaqStaticContentClient(DOMAIN_API_URL, undefined, 1);
		eventAggregationService.subscribeTo(SearchPageCacheReadyEvent.EventType, this, true);
	}

	public notify(event: SearchPageCacheReadyEvent): void {
		this.cache = event.cache;
	}

	public async getContent(filters: Filter[]): Promise<Content | null> {
		try {
			const result = await this.get(filters);

			if (!result) {
				return null;
			}

			const staticContent = result.getSearchStaticContent()!;

			return new Content(
				result.getFaqId()!.toString(),
				result.getSearchFaqs() ?? [],
				staticContent[0].getText() ?? "",
				staticContent[0].getUrls() ?? []
			);

		} catch (error: any) {
			console.warn("An error occurred when fetching FAQ static content:", error);
			return null;
		}
	}

	private async get(filters: Filter[]): Promise<IFaqStaticContent | null> {
		const groguFilters = this.getMappedFilters(filters);
		const selectedFiltersCount = filters.filter(f => f.values.length > 0).length;

		if (!this.shouldLoadContent(groguFilters, selectedFiltersCount)) {
			return null;
		}

		const key = this.generateKey(groguFilters);
		const hash = await this.hash(key);

		const cacheKey = `domain-api/programmes/faq-static-content/${hash}`;

		const cachedContent = this.cache?.get<IFaqStaticContent>(cacheKey);
		if (cachedContent) {
			return cachedContent;
		}

		const content = await this.client.fetch(hash);
		this.cache?.set(cacheKey, content);

		return content;
	}

	private getMappedFilters(filters: Filter[]): GroguFilterKeys {
		return filters.reduce((acc, filter) => {
			const mappedKey = groguPropertyMapper[filter.key];

			if (!mappedKey) {
				return acc;
			}

			const values = filter.values;
			let value = values.length === 1 ? values[0] : undefined;

			if (mappedKey === 'tuitionFee' && value === TuitionFeeFilterOptionValue.ZERO_TO_TWO_THOUSAND_FIVE_HUNDRED) {
				value = 'affordable';
			}

			return {
				...acc,
				[mappedKey]: value
			};
		}, {} as GroguFilterKeys);
	}


	private generateKey(filters: GroguFilterKeys): string {
		const keyOrder = [
			'countryId',
			'continentId',
			'disciplineId',
			'attendance',
			'tuitionFee'
		];

		const keyPairs = keyOrder.reduce((acc, key) => {
			const value = filters[key as keyof GroguFilterKeys];

			if (value === undefined) {
				return acc;
			}

			const keyValuePair = `${key}:${value}`;
			acc.push(keyValuePair);

			return acc;
		}, [] as string[]);

		return keyPairs.length > 0 ? `-${keyPairs.join('-')}` : '';
	}

	private async hash(key: string): Promise<number> {
		const encoder = new TextEncoder();

		const data = encoder.encode(key);
		const hashBuffer = await window.crypto.subtle.digest('SHA-256', data);
		const dataView = new DataView(hashBuffer);

		return dataView.getUint32(0, false);
	}

	private shouldLoadContent(filters: GroguFilterKeys, originalFilterCount: number): boolean {
		const isAffordable = filters.tuitionFee === 'affordable';
		const isOnline = filters.attendance === String(AttendanceFilterOptionValue.ONLINE);
		const hasContinent = filters.continentId !== undefined;
		const hasCountry = filters.countryId !== undefined;
		const hasDiscipline = filters.disciplineId !== undefined;

		if (!isAffordable && !isOnline) {
			return false;
		}

		const mappedFilterCount = Object.values(filters).filter(value => value !== undefined).length;

		if (mappedFilterCount !== originalFilterCount || mappedFilterCount > 2) {
			return false;
		}

		if (mappedFilterCount === 1 || (isAffordable && isOnline)) {
			return true;
		}

		return hasContinent || hasCountry || hasDiscipline;
	}
}

export default new FaqStaticContentPresenter(EventAggregationService.instance);
